import React, { useEffect, useState } from 'react';
import { Loader2, Plus, Syringe } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from '@/hooks/use-toast';
import { useClinic } from '@/contexts/ClinicContext';
import {
  immunizationsService,
  PatientImmunization,
  CommonVaccines,
} from '@/services/immunizationsService';

interface PatientImmunizationsTabProps {
  patientId: string;
}

const PatientImmunizationsTab = ({ patientId }: PatientImmunizationsTabProps) => {
  const { clinic, isPractitioner, isAdmin } = useClinic();
  const canWrite = isPractitioner || isAdmin;

  const [immunizations, setImmunizations] = useState<PatientImmunization[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [form, setForm] = useState({
    vaccinePreset: '',
    vaccineCode: '',
    vaccineDisplay: '',
    occurrenceDate: new Date().toISOString().slice(0, 10),
    lotNumber: '',
    site: '',
    route: '',
    doseNumber: '',
    note: '',
  });

  const refetch = async () => {
    if (!clinic?.id) return;
    setIsLoading(true);
    try {
      const rows = await immunizationsService.getPatientImmunizations(clinic.id, patientId);
      setImmunizations(rows);
    } catch (error: any) {
      toast({
        title: 'Failed to load immunizations',
        description: error?.response?.data?.error?.message || error.message,
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (patientId) refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, clinic?.id]);

  const resetForm = () => setForm({
    vaccinePreset: '',
    vaccineCode: '',
    vaccineDisplay: '',
    occurrenceDate: new Date().toISOString().slice(0, 10),
    lotNumber: '',
    site: '',
    route: '',
    doseNumber: '',
    note: '',
  });

  const applyPreset = (presetKey: string) => {
    const preset = (CommonVaccines as any)[presetKey];
    if (preset) {
      setForm(prev => ({
        ...prev,
        vaccinePreset: presetKey,
        vaccineCode: preset.code,
        vaccineDisplay: preset.display,
      }));
    }
  };

  const handleAdd = async () => {
    if (!clinic?.id || !form.vaccineCode.trim() || !form.vaccineDisplay.trim()) return;
    setIsSaving(true);
    try {
      await immunizationsService.createPatientImmunization(clinic.id, patientId, {
        vaccineCode: form.vaccineCode.trim(),
        vaccineDisplay: form.vaccineDisplay.trim(),
        occurrenceDate: form.occurrenceDate
          ? new Date(form.occurrenceDate).toISOString()
          : new Date().toISOString(),
        lotNumber: form.lotNumber.trim() || undefined,
        site: form.site.trim() || undefined,
        route: form.route.trim() || undefined,
        doseNumber: form.doseNumber ? parseInt(form.doseNumber, 10) : undefined,
        status: 'completed',
        note: form.note.trim() || undefined,
      });
      toast({ title: 'Immunization recorded', variant: 'success' });
      setIsAddOpen(false);
      resetForm();
      refetch();
    } catch (error: any) {
      toast({
        title: 'Failed to record immunization',
        description: error?.response?.data?.error?.message || error.message,
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const completed = immunizations.filter(i => i.status === 'completed');
  const otherStatus = immunizations.filter(i => i.status !== 'completed');

  return (
    <>
      <div className="bg-white rounded-lg border border-slate-200">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-sky-500">
              <Syringe className="h-4 w-4 text-white" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-slate-900">Immunizations</h3>
              <p className="text-xs text-slate-500">
                {isLoading ? 'Loading…' : `${completed.length} recorded`}
              </p>
            </div>
          </div>
          {canWrite && (
            <Button size="sm" onClick={() => setIsAddOpen(true)} className="rounded-lg">
              <Plus className="h-4 w-4 mr-1.5" />
              Record immunization
            </Button>
          )}
        </div>

        <div className="p-5 space-y-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-sky-500" />
            </div>
          ) : completed.length === 0 ? (
            <div className="text-sm text-slate-500 italic">No immunizations on record</div>
          ) : (
            <div className="space-y-2">
              {completed.map(i => (
                <ImmunizationRow key={i.id} immunization={i} />
              ))}
            </div>
          )}

          {otherStatus.length > 0 && (
            <div className="space-y-2">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                Other status
              </div>
              <div className="space-y-2">
                {otherStatus.map(i => (
                  <ImmunizationRow key={i.id} immunization={i} muted />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <Sheet open={isAddOpen} onOpenChange={setIsAddOpen}>
        <SheetContent className="w-full !max-w-[480px] p-0 flex flex-col">
          <SheetHeader className="px-6 py-4 border-b border-slate-200">
            <SheetTitle>Record Immunization</SheetTitle>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto px-6 py-6 space-y-4">
            <div className="space-y-1.5">
              <Label>Common vaccines</Label>
              <Select value={form.vaccinePreset} onValueChange={applyPreset} disabled={isSaving}>
                <SelectTrigger>
                  <SelectValue placeholder="Pick a preset or enter CVX below" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(CommonVaccines).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v.display}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="code">CVX code</Label>
                <Input
                  id="code"
                  placeholder="e.g., 150"
                  value={form.vaccineCode}
                  onChange={(e) => setForm(prev => ({ ...prev, vaccineCode: e.target.value }))}
                  disabled={isSaving}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="date">Given on</Label>
                <Input
                  id="date"
                  type="date"
                  value={form.occurrenceDate}
                  onChange={(e) => setForm(prev => ({ ...prev, occurrenceDate: e.target.value }))}
                  disabled={isSaving}
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="display">Vaccine name</Label>
              <Input
                id="display"
                placeholder="e.g., Influenza quadrivalent"
                value={form.vaccineDisplay}
                onChange={(e) => setForm(prev => ({ ...prev, vaccineDisplay: e.target.value }))}
                disabled={isSaving}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="lot">Lot number</Label>
                <Input
                  id="lot"
                  value={form.lotNumber}
                  onChange={(e) => setForm(prev => ({ ...prev, lotNumber: e.target.value }))}
                  disabled={isSaving}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="dose">Dose #</Label>
                <Input
                  id="dose"
                  type="number"
                  min="1"
                  value={form.doseNumber}
                  onChange={(e) => setForm(prev => ({ ...prev, doseNumber: e.target.value }))}
                  disabled={isSaving}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="site">Site</Label>
                <Input
                  id="site"
                  placeholder="left deltoid"
                  value={form.site}
                  onChange={(e) => setForm(prev => ({ ...prev, site: e.target.value }))}
                  disabled={isSaving}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="route">Route</Label>
                <Input
                  id="route"
                  placeholder="IM"
                  value={form.route}
                  onChange={(e) => setForm(prev => ({ ...prev, route: e.target.value }))}
                  disabled={isSaving}
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="note">Notes</Label>
              <textarea
                id="note"
                className="w-full min-h-[80px] rounded-md border border-slate-200 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400"
                value={form.note}
                onChange={(e) => setForm(prev => ({ ...prev, note: e.target.value }))}
                disabled={isSaving}
              />
            </div>
          </div>
          <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between">
            <Button variant="ghost" onClick={() => setIsAddOpen(false)} disabled={isSaving}>
              Cancel
            </Button>
            <Button
              onClick={handleAdd}
              disabled={!form.vaccineDisplay.trim() || !form.vaccineCode.trim() || isSaving}
              className="rounded-lg"
            >
              {isSaving ? (
                <span className="flex items-center gap-1.5">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Saving…
                </span>
              ) : 'Record'}
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};

const ImmunizationRow: React.FC<{
  immunization: PatientImmunization;
  muted?: boolean;
}> = ({ immunization: i, muted }) => {
  return (
    <div className={`border border-slate-200 rounded-lg p-3 ${muted ? 'bg-slate-50' : 'bg-white'}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`text-sm font-medium ${muted ? 'text-slate-500' : 'text-slate-900'}`}>
          {i.vaccine_display}
        </span>
        <span className="text-xs text-slate-400 font-mono">CVX {i.vaccine_code}</span>
        {i.dose_number && (
          <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 text-xs font-medium">
            Dose {i.dose_number}{i.series_doses ? ` / ${i.series_doses}` : ''}
          </span>
        )}
        {i.status !== 'completed' && (
          <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-xs font-medium">
            {i.status.replace('-', ' ')}
          </span>
        )}
      </div>
      <div className="mt-1 flex items-center gap-2 text-xs text-slate-500 flex-wrap">
        <span>{new Date(i.occurrence_date).toLocaleDateString()}</span>
        {i.lot_number && <span>· lot {i.lot_number}</span>}
        {i.site && <span>· {i.site}</span>}
        {i.route && <span>· {i.route}</span>}
        {i.performer && <span>· {i.performer.first_name} {i.performer.last_name}</span>}
      </div>
      {i.note && (
        <p className="mt-2 text-xs text-slate-600 leading-relaxed">{i.note}</p>
      )}
    </div>
  );
};

export default PatientImmunizationsTab;
